import { FunctionComponent, memo, useEffect, useMemo, useState } from "react";
import { IProps } from "./interaction-modal-with-previews.types";
import Modal from "@edgetier/modal";
import InteractionPreviews from "../interaction-modal/interaction-previews";
import { IInteraction } from "@edgetier/watchtower-types";
import InteractionModalContent from "../interaction-modal-content";
import { defaultContext as defaultInteractionContext } from "components-for/interactions/interaction-context";
import { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import { useModalKeyboardShortcut } from "hooks-for/modal/use-modal-keyboard-shortcut";
import { getSurroundingElements } from "utilities/get-surrounding-elements";
import InteractionSummary from "../interaction-summary";
import "./interaction-modal-with-previews.scss";

/**
 * A modal for displaying an interaction with previews of the previous and next interactions above and below it.
 * @param props.areWritingIssuesVisible    Are writing issues visible in the interaction messages.
 * @param props.initialVisibleInteractions The interaction that the user first choose from the sample interactions.
 * @param props.interactions               All of the sample interactions.
 * @param props.modalProps                 Props needed to render the modal.
 * @returns
 */
const InteractionModalWithPreviews: FunctionComponent<IProps> = ({
    areWritingIssuesVisible = defaultInteractionContext.areWritingIssuesVisible,
    interaction: initialVisibleInteraction,
    interactions,
    parameters,
    loadMoreInteractions,
    ...modalProps
}) => {
    const [visibleInteraction, setVisibleInteraction] = useState<IInteraction>(initialVisibleInteraction);
    const { data: interactionsCount } = useInteractionsCount(parameters ?? {});

    const [interactionsBefore, interactionsAfter] = useMemo(() => {
        return getSurroundingElements<IInteraction>(interactions.indexOf(visibleInteraction), interactions);
    }, [visibleInteraction, interactions]);

    useEffect(() => {
        setVisibleInteraction(initialVisibleInteraction);
    }, [initialVisibleInteraction]);

    useModalKeyboardShortcut<IInteraction>(
        interactions,
        visibleInteraction,
        setVisibleInteraction,
        loadMoreInteractions
    );

    return (
        <Modal size="super-wide" {...modalProps}>
            <InteractionPreviews<IInteraction>
                previewItems={interactionsBefore}
                onPreviewClick={setVisibleInteraction}
                position="before"
            >
                {(interaction) => (
                    <div className="interaction-previews__text">
                        <InteractionSummary
                            contentCleaned={interaction.contentCleaned}
                            startDateTime={interaction.startDateTime}
                        />
                    </div>
                )}
            </InteractionPreviews>
            <InteractionModalContent
                interactionId={visibleInteraction.interactionId}
                areWritingIssuesVisible={areWritingIssuesVisible}
                interactionIndex={interactions.indexOf(visibleInteraction)}
                interactionsCount={interactionsCount}
            />
            <InteractionPreviews<IInteraction>
                previewItems={interactionsAfter}
                onPreviewClick={setVisibleInteraction}
                position="after"
                loadMoreItems={loadMoreInteractions}
            >
                {(interaction) => (
                    <div className="interaction-previews__text">
                        <InteractionSummary
                            contentCleaned={interaction.contentCleaned}
                            startDateTime={interaction.startDateTime}
                        />
                    </div>
                )}
            </InteractionPreviews>
        </Modal>
    );
};

export default memo(InteractionModalWithPreviews);
